import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { ContentSchema, RoomOpenGraph } from '../../definitions/schemas';
import { UrlViewerProps } from './UrlViewer';
import RenderIframe from './RenderIframe';
import { getAspectRatioHeight } from '../../lib/Media';
import { getNumericSpacing } from '../../lib/Styles';
import RoomContentWrapper, {
  RoomContentWrapperProps,
} from '../Rooms/Content/RoomContentWrapper';
import useShowCover from '../../lib/hook/useShowCover';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import { StorageProviderMTPickers } from '../../enums/Integrations/StorageProviders';
import FederatedAssetHubViewer from './FederatedAssetHubViewer';
import { THEATER_FRAME_HEIGHT } from './helper';
import useTheaterFrameHeight from '../../lib/hook/useTheaterFrameHeight';

const HEIGHT_OFFSET = 1.2;
const HEIGHT_OFFSET_DOWNLOADABLE = 44;

enum UrlParams {
  AUTO_PLAY_MEDIA = 'autoPlayMedia',
}

type PickedUrlViewerProps = Omit<UrlViewerProps, 'onClick'>;

export interface IframeUrlViewerProps
  extends PickedUrlViewerProps,
    RoomContentWrapperProps {
  openGraph?: RoomOpenGraph;
  onWindowMessageReceived: (event: MessageEvent) => void;
  width?: number;
  content: Partial<ContentSchema>;
  /** True when rendered inside theater mode instead of a room tile */
  forceFullScreen?: boolean;
}

const IframeUrlViewer: React.FC<IframeUrlViewerProps> = (
  props: IframeUrlViewerProps
): JSX.Element => {
  const {
    iframe,
    name,
    openGraph,
    url,
    onWindowMessageReceived,
    width,
    cover,
    disableDefaultCover,
    content,
    rounded,
    forceFullScreen,
  } = props;

  const isFederatedAssetEnabled = useFeatureFlag(
    OrganizationFlagEnum.FEDERATED_ASSET_ENABLED
  );

  // only the setter is needed — the value is consumed by RoomContentWrapper
  const [, setAutoPlay] = useState(false);
  const theme = useTheme();
  // Room tiles keep the width-based height (the page scroll reaches them);
  // in theater mode the frame takes the measured remaining space so the
  // viewer's bottom zoom toolbar stays visible without a second scrollbar
  // and without leaving a gap (PCUTS-260)
  const { ref: theaterRef, height: theaterHeight } = useTheaterFrameHeight(
    !!forceFullScreen
  );
  const height = forceFullScreen
    ? theaterHeight ?? THEATER_FRAME_HEIGHT
    : (width
        ? getAspectRatioHeight(width) + HEIGHT_OFFSET_DOWNLOADABLE
        : getNumericSpacing(theme, 44)) * HEIGHT_OFFSET;

  const isAssetHubContentProvider =
    content?.mtData?.provider === StorageProviderMTPickers.AssetHub;

  const isInlineIframeViewerEnabled = useFeatureFlag(
    OrganizationFlagEnum.INLINE_IFRAME_VIEWER
  );

  const { showCover } = useShowCover({
    content,
    isAssetHubContent: true,
    documentCoverEditable: false,
    isInlineIframeViewerEnabled,
  });

  const urlIframe = useMemo(() => {
    if (openGraph?.videoSecureUrl) {
      return openGraph.videoSecureUrl;
    }

    const newUrl = new URL(url);
    const queryParams = newUrl.searchParams;
    if (showCover) {
      queryParams.set(UrlParams.AUTO_PLAY_MEDIA, 'true');
    }
    newUrl.search = queryParams.toString();
    const newUrlString = newUrl.toString();

    // URLs without protocol will load relative to our domain
    // so we append the protocol to force the iframe to externalize the url
    if (newUrlString.startsWith('http')) {
      return newUrlString;
    }

    return `https://${newUrlString}`;
  }, [openGraph, url, showCover]);

  if (isAssetHubContentProvider && isFederatedAssetEnabled) {
    return (
      <FederatedAssetHubViewer
        url={url}
        name={name}
        height={forceFullScreen ? undefined : height}
        forceFullScreen={forceFullScreen}
        onWindowMessageReceived={onWindowMessageReceived}
        cover={cover}
        disableDefaultCover={disableDefaultCover}
        rounded={rounded}
        width={width}
        content={content}
        setAutoPlay={setAutoPlay}
      />
    );
  }

  return (
    <IframeUrlViewerWrapper
      showCover={showCover}
      disableDefaultCover={disableDefaultCover}
      rounded={rounded}
      width={width}
      setAutoPlay={setAutoPlay}
      cover={cover}
    >
      {iframe && (
        <RenderIframe
          name={name}
          onWindowMessageReceived={onWindowMessageReceived}
          urlIframe={urlIframe}
          height={height}
          roundedCorners
          wrapperRef={theaterRef}
        />
      )}
    </IframeUrlViewerWrapper>
  );
};

const IframeUrlViewerWrapper = ({
  children,
  showCover,
  disableDefaultCover,
  rounded,
  width,
  setAutoPlay,
  cover,
}: {
  children: any;
  showCover: boolean;
  disableDefaultCover?: boolean;
  rounded?: boolean;
  width?: number;
  setAutoPlay: (autoPlay: boolean) => void;
  cover: any;
}) =>
  showCover ? (
    <RoomContentWrapper
      cover={cover}
      disableDefaultCover={disableDefaultCover}
      rounded={rounded}
      width={width}
      setAutoPlay={setAutoPlay}
    >
      {children}
    </RoomContentWrapper>
  ) : (
    children
  );

export default IframeUrlViewer;
